import type { CheckoutPlanLike } from './checkout.types';
import { CYCLE_LABELS } from '../../components/students/PaymentDetails/paymentDetails.constants';
import { CHECKOUT_STRIPE_PUBLIC_ERROR_MESSAGE } from './checkout.constants';
import { resolveCallingCode } from '../../constants/callingCodes';
import { maskCpf, maskPhoneByCountry, maskPostalCode, maskCardNumber, maskCardExpiry, maskCardCvv, unmaskCpf, unmaskPhone, unmaskCardNumber, isValidIsoDate } from '../../utils/masks';
import { validateEmail, validateName, validatePhone, validateCpf } from '../../utils/validation';
import type {
  CheckoutAddressData,
  CheckoutCardData,
  CheckoutFormState,
  CheckoutGateway,
  CheckoutStudentPrefillData,
  LeadQualificationFormState,
  CheckoutPaymentResult,
  CheckoutPersonalData,
  CheckoutPayerData,
  CheckoutSelectedPlan,
  CheckoutStepId,
  CheckoutSubmissionPayload,
  PaymentMethod,
} from './checkout.types';

export type CheckoutAvailabilityField = 'email' | 'cpf' | 'phone';

export const isBrazilCheckoutCountry = (country: string | undefined | null): boolean => {
  const normalized = (country ?? '').trim().toUpperCase();
  return !normalized || normalized === 'BR';
};

export const resolveCheckoutPaymentProvider = (
  country: string | undefined | null,
): CheckoutGateway => (
  (country ?? '').trim().toUpperCase() === 'BR' ? 'asaas' : 'stripe'
);

const STRIPE_OBJECT_ID_PATTERN =
  /\b(?:cs|pi|po|seti|cus|sub|in|price|prod|pm|tok|ch|re|src|sk|pk|rk|acct)_(?:test_|live_)?[A-Za-z0-9]+(?:_secret_[A-Za-z0-9]+)?\b/i;
const STRIPE_TECHNICAL_ERROR_PATTERN =
  /no such |resource[_ ]missing|invalid api key|ui_mode|client[_ ]secret|checkout\.session|payment_intent/i;

export const toPublicCheckoutErrorMessage = (
  message: string | null | undefined,
  fallback = CHECKOUT_STRIPE_PUBLIC_ERROR_MESSAGE,
): string => {
  const trimmed = (message ?? '').trim();
  if (!trimmed) {
    return fallback;
  }

  if (STRIPE_OBJECT_ID_PATTERN.test(trimmed) || STRIPE_TECHNICAL_ERROR_PATTERN.test(trimmed)) {
    return fallback;
  }

  return trimmed;
};

export const applyCheckoutPrefillToForm = (
  prefill: CheckoutStudentPrefillData,
): Pick<CheckoutFormState, 'personal' | 'address'> => {
  const callingCode = resolveCallingCode(prefill.personal.callingCode || '55');

  return {
    personal: {
      fullName: prefill.personal.fullName,
      email: prefill.personal.email,
      cpf: maskCpf(prefill.personal.cpf),
      callingCode,
      phone: maskPhoneByCountry(prefill.personal.phone, callingCode),
      birthDate: prefill.personal.birthDate,
    },
    address: {
      cep: maskPostalCode(prefill.address.cep, prefill.address.country || 'BR'),
      street: prefill.address.street,
      number: prefill.address.number,
      complement: prefill.address.complement,
      neighborhood: prefill.address.neighborhood,
      city: prefill.address.city,
      state: prefill.address.state,
      country: prefill.address.country || 'BR',
    },
  };
};

const requireText = (value: string, message: string): string => {
  if (!value.trim()) {
    return message;
  }

  return '';
};

export const trimCheckoutFullName = (value: string): string => value.trim();

export const maskCheckoutPersonalField = (
  field: keyof CheckoutPersonalData,
  value: string,
  callingCode: string,
  country: string,
): string => {
  switch (field) {
    case 'fullName':
      return value;
    case 'cpf':
      return maskCpf(value);
    case 'phone':
      return maskPhoneByCountry(value, callingCode, { preferMobile: true });
    default:
      return value;
  }
};

export const maskCheckoutAddressField = (
  field: keyof CheckoutAddressData,
  value: string,
  country: string,
): string => {
  if (field === 'cep') {
    return maskPostalCode(value, country);
  }

  return value;
};

export const maskCheckoutCardField = (
  field: keyof CheckoutCardData,
  value: string,
): string => {
  switch (field) {
    case 'number':
      return maskCardNumber(value);
    case 'expiry':
      return maskCardExpiry(value);
    case 'cvv':
      return maskCardCvv(value);
    default:
      return value;
  }
};

export const maskCheckoutPayerField = (
  field: keyof CheckoutPayerData,
  value: string,
  callingCode: string,
): string => {
  switch (field) {
    case 'cpf':
      return maskCpf(value);
    case 'postalCode':
      return maskPostalCode(value, 'BR');
    case 'phone':
      return maskPhoneByCountry(value, callingCode);
    default:
      return value;
  }
};

/** Copia os dados da aluna (pessoais + endereço) para o pagador. */
export const copyCheckoutPayerFromStudent = (
  personal: CheckoutPersonalData,
  address: CheckoutAddressData,
): CheckoutPayerData => ({
  name: personal.fullName,
  email: personal.email,
  cpf: personal.cpf,
  postalCode: address.cep,
  addressNumber: address.number,
  addressComplement: address.complement,
  callingCode: personal.callingCode || '55',
  phone: personal.phone,
});

/**
 * Prefill dos dados do pagador a partir da aluna, preservando o que já foi digitado
 * nos campos vazios (uso ao desmarcar “usar dados da aluna”).
 */
export const buildCheckoutPayerFromPersonalAndAddress = (
  personal: CheckoutPersonalData,
  address: CheckoutAddressData,
  current?: CheckoutPayerData | null,
): CheckoutPayerData => {
  const fromStudent = copyCheckoutPayerFromStudent(personal, address);
  const fallback = current ?? {
    name: '',
    email: '',
    cpf: '',
    postalCode: '',
    addressNumber: '',
    addressComplement: '',
    callingCode: '55',
    phone: '',
  };

  const pick = (currentValue: string, nextValue: string): string => (
    currentValue.trim() ? currentValue : nextValue
  );

  return {
    name: pick(fallback.name, fromStudent.name),
    email: pick(fallback.email, fromStudent.email),
    cpf: pick(fallback.cpf, fromStudent.cpf),
    postalCode: pick(fallback.postalCode, fromStudent.postalCode),
    addressNumber: pick(fallback.addressNumber, fromStudent.addressNumber),
    addressComplement: pick(fallback.addressComplement, fromStudent.addressComplement),
    callingCode: pick(fallback.callingCode, fromStudent.callingCode),
    phone: pick(fallback.phone, fromStudent.phone),
  };
};

export const resolveCheckoutPayerForSubmission = (form: CheckoutFormState): CheckoutPayerData => (
  form.useStudentDataAsPayer
    ? copyCheckoutPayerFromStudent(form.personal, form.address)
    : form.payer
);

const isValidCardExpiry = (expiry: string): boolean => {
  const match = expiry.trim().match(/^(\d{2})\/(\d{2})$/);
  if (!match) {
    return false;
  }

  const month = Number.parseInt(match[1], 10);
  const year = Number.parseInt(match[2], 10);

  if (month < 1 || month > 12) {
    return false;
  }

  const now = new Date();
  const currentYear = now.getFullYear() % 100;
  const currentMonth = now.getMonth() + 1;

  if (year < currentYear) {
    return false;
  }

  if (year === currentYear && month < currentMonth) {
    return false;
  }

  return true;
};

/** Luhn check — rejects arbitrary / non-existent card number sequences. */
export const passesLuhnCheck = (digits: string): boolean => {
  if (!/^\d{13,19}$/.test(digits)) {
    return false;
  }

  let sum = 0;
  let shouldDouble = false;

  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = Number(digits[index]);

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};

export const isValidCreditCardNumber = (cardNumber: string): boolean => {
  const digits = unmaskCardNumber(cardNumber);
  if (digits.length < 13 || digits.length > 16) {
    return false;
  }

  // All-zero sequences pass Luhn but are never real cards.
  if (/^0+$/.test(digits)) {
    return false;
  }

  return passesLuhnCheck(digits);
};

export const validateCardData = (card: CheckoutCardData): Record<string, string> => {
  const errors: Record<string, string> = {};

  const holderNameError = requireText(card.holderName, 'Informe o nome impresso no cartão.');
  if (holderNameError) {
    errors.cardHolderName = holderNameError;
  }

  const cardDigits = unmaskCardNumber(card.number);
  if (!cardDigits) {
    errors.cardNumber = 'Informe o número do cartão.';
  } else if (!isValidCreditCardNumber(cardDigits)) {
    errors.cardNumber = 'Informe um cartão de crédito válido.';
  }

  if (!card.expiry.trim()) {
    errors.cardExpiry = 'Informe a validade do cartão.';
  } else if (!isValidCardExpiry(card.expiry)) {
    errors.cardExpiry = 'Informe uma validade válida (MM/AA).';
  }

  if (card.cvv.length < 3) {
    errors.cardCvv = 'Informe o CVV do cartão.';
  }

  return errors;
};

export const validatePayerData = (payer: CheckoutPayerData): Record<string, string> => {
  const errors: Record<string, string> = {};

  const nameError = validateName(payer.name);
  if (nameError) {
    errors.payerName = nameError;
  }

  const emailError = validateEmail(payer.email);
  if (emailError) {
    errors.payerEmail = emailError;
  }

  const cpfError = validateCpf(payer.cpf, { required: true });
  if (cpfError) {
    errors.payerCpf = cpfError;
  }

  const postalDigits = payer.postalCode.replace(/\D/g, '');
  if (!postalDigits) {
    errors.payerPostalCode = 'Informe o CEP brasileiro do pagador.';
  } else if (postalDigits.length !== 8) {
    errors.payerPostalCode = 'O Asaas aceita apenas CEP brasileiro com 8 dígitos.';
  }

  if (!payer.addressNumber.trim()) {
    errors.payerAddressNumber = 'Informe o número do endereço.';
  }

  const phoneError = validatePhone(payer.phone, payer.callingCode);
  if (phoneError) {
    errors.payerPhone = phoneError;
  }

  return errors;
};

export const validatePersonalDataStep = (
  data: CheckoutPersonalData,
  addressCountry?: string,
): Record<string, string> => {
  const errors: Record<string, string> = {};

  const nameError = validateName(data.fullName);
  if (nameError) {
    errors.fullName = nameError;
  }

  const emailError = validateEmail(data.email);
  if (emailError) {
    errors.email = emailError;
  }

  const cpfError = validateCpf(data.cpf, { required: isBrazilCheckoutCountry(addressCountry) });
  if (cpfError) {
    errors.cpf = cpfError;
  }

  const phoneError = validatePhone(data.phone, data.callingCode);
  if (phoneError) {
    errors.phone = phoneError.replace(/telefone/gi, 'celular');
  }

  if (!data.birthDate) {
    errors.birthDate = 'A data de nascimento é obrigatória';
  } else if (!isValidIsoDate(data.birthDate)) {
    errors.birthDate = 'Digite uma data válida (DD/MM/AAAA)';
  }

  return errors;
};

export const validateLeadQualificationPersonalData = (
  data: CheckoutPersonalData,
): Record<string, string> => {
  const errors: Record<string, string> = {};

  const nameError = validateName(data.fullName);
  if (nameError) {
    errors.fullName = nameError;
  }

  const emailError = validateEmail(data.email);
  if (emailError) {
    errors.email = emailError;
  }

  const phoneError = validatePhone(data.phone, data.callingCode, { requireMobile: true });
  if (phoneError) {
    errors.phone = phoneError.replace(/telefone/gi, 'WhatsApp');
  }

  return errors;
};

export const validateLeadQualificationForm = (
  form: LeadQualificationFormState,
): Record<string, string> => {
  const errors = validateLeadQualificationPersonalData(form.personal);

  if (!form.investmentInterest) {
    errors.investmentInterest = 'Selecione uma opção.';
  }

  if (!form.startTimeExpectation) {
    errors.startTimeExpectation = 'Selecione uma opção.';
  }

  if (!form.mainGoal) {
    errors.mainGoal = 'Selecione uma opção.';
  }

  if (!form.trainingRelationship) {
    errors.trainingRelationship = 'Selecione uma opção.';
  }

  if (!form.trainingExperience) {
    errors.trainingExperience = 'Selecione uma opção.';
  }

  if (!form.busyRoutineBehavior) {
    errors.busyRoutineBehavior = 'Selecione uma opção.';
  }

  if (!form.weeklyCommitmentDays) {
    errors.weeklyCommitmentDays = 'Selecione uma opção.';
  }

  if (!form.trainingFormat) {
    errors.trainingFormat = 'Selecione uma opção.';
  }

  if (!form.consultingExpectation) {
    errors.consultingExpectation = 'Selecione uma opção.';
  }

  if (!form.injuryLimitation) {
    errors.injuryLimitation = 'Selecione uma opção.';
  }

  return errors;
};

export const validateAddressStep = (data: CheckoutAddressData): Record<string, string> => {
  const errors: Record<string, string> = {};

  const cepDigits = data.cep.replace(/\D/g, '');
  if (!cepDigits) {
    errors.cep = 'O CEP é obrigatório';
  } else if (data.country === 'BR' && cepDigits.length !== 8) {
    errors.cep = 'Digite um CEP válido';
  }

  const streetError = requireText(data.street, 'A rua é obrigatória');
  if (streetError) {
    errors.street = streetError;
  }

  const numberError = requireText(data.number, 'O número é obrigatório');
  if (numberError) {
    errors.number = numberError;
  }

  const neighborhoodError = requireText(data.neighborhood, 'O bairro é obrigatório');
  if (neighborhoodError) {
    errors.neighborhood = neighborhoodError;
  }

  const cityError = requireText(data.city, 'A cidade é obrigatória');
  if (cityError) {
    errors.city = cityError;
  }

  const stateError = requireText(data.state, 'O estado é obrigatório');
  if (stateError) {
    errors.state = stateError;
  }

  const countryError = requireText(data.country, 'O país é obrigatório');
  if (countryError) {
    errors.country = countryError;
  }

  return errors;
};

export const getCheckoutAvailabilityFieldError = (
  field: CheckoutAvailabilityField,
  data: CheckoutPersonalData,
  addressCountry?: string,
): string => {
  if (field === 'cpf' && !isBrazilCheckoutCountry(addressCountry)) {
    return '';
  }

  const errors = validatePersonalDataStep(data, addressCountry);
  return errors[field] ?? '';
};

export const buildCheckoutAvailabilityValue = (
  field: CheckoutAvailabilityField,
  data: CheckoutPersonalData,
  addressCountry?: string,
): { value: string; callingCode?: string } | null => {
  if (field === 'email') {
    const email = data.email.trim();
    return email ? { value: email } : null;
  }

  if (field === 'cpf') {
    if (!isBrazilCheckoutCountry(addressCountry)) {
      return null;
    }

    const cpfError = validateCpf(data.cpf, { required: true });
    if (cpfError) {
      return null;
    }

    return { value: unmaskCpf(data.cpf) };
  }

  const phone = unmaskPhone(data.phone);
  if (validatePhone(data.phone, data.callingCode)) {
    return null;
  }

  const callingCode = data.callingCode.split('_')[0].replace(/\D/g, '') || '55';
  return { value: phone, callingCode };
};

export const validatePlanStep = (planId: number | null): Record<string, string> => {
  if (!planId) {
    return { planId: 'Selecione um plano para continuar.' };
  }

  return {};
};

export const validatePaymentStep = (
  paymentMethod: PaymentMethod | null,
  card: CheckoutCardData,
  payer?: CheckoutPayerData,
  useStudentDataAsPayer = false,
  personal?: CheckoutPersonalData,
  address?: CheckoutAddressData,
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!paymentMethod) {
    errors.paymentMethod = 'Selecione uma forma de pagamento.';
    return errors;
  }

  if (address && resolveCheckoutPaymentProvider(address.country) === 'stripe') {
    if (paymentMethod !== 'credit_card') {
      errors.paymentMethod = 'Pagamentos fora do Brasil são processados pelo Stripe.';
    }
    return errors;
  }

  if (paymentMethod === 'credit_card') {
    const resolvedPayer = useStudentDataAsPayer && personal && address
      ? copyCheckoutPayerFromStudent(personal, address)
      : payer;

    return {
      ...errors,
      ...validateCardData(card),
      ...(resolvedPayer ? validatePayerData(resolvedPayer) : {}),
    };
  }

  return errors;
};

export const validateCheckoutForm = (form: CheckoutFormState): Record<string, string> => ({
  ...validatePersonalDataStep(form.personal, form.address.country),
  ...validateAddressStep(form.address),
  ...validatePaymentStep(
    form.paymentMethod,
    form.card,
    form.payer,
    form.useStudentDataAsPayer,
    form.personal,
    form.address,
  ),
});

export const CHECKOUT_FORM_ERROR_FOCUS_ORDER = [
  'email',
  'fullName',
  'birthDate',
  'cpf',
  'phone',
  'country',
  'cep',
  'state',
  'city',
  'neighborhood',
  'street',
  'number',
  'complement',
  'paymentMethod',
  'cardHolderName',
  'cardNumber',
  'cardExpiry',
  'cardCvv',
  'payerName',
  'payerEmail',
  'payerCpf',
  'payerPostalCode',
  'payerAddressNumber',
  'payerPhone',
] as const;

export type CheckoutFormErrorFocusField = (typeof CHECKOUT_FORM_ERROR_FOCUS_ORDER)[number];

const CHECKOUT_PAYER_ERROR_FIELDS: readonly CheckoutFormErrorFocusField[] = [
  'payerName',
  'payerEmail',
  'payerCpf',
  'payerPostalCode',
  'payerAddressNumber',
  'payerPhone',
];

export const checkoutFormHasPayerErrors = (formErrors: Record<string, string>): boolean => (
  CHECKOUT_PAYER_ERROR_FIELDS.some((field) => Boolean(formErrors[field]?.trim()))
);

export const getFirstCheckoutFormError = (
  formErrors: Record<string, string>,
): { field: string; message: string } | null => {
  for (const field of CHECKOUT_FORM_ERROR_FOCUS_ORDER) {
    const message = formErrors[field]?.trim();
    if (message) {
      return { field, message };
    }
  }

  const fallback = Object.entries(formErrors).find(([, message]) => Boolean(message?.trim()));
  return fallback ? { field: fallback[0], message: fallback[1].trim() } : null;
};

/** Validação do checkout resumido: só pagamento (e pagador no cartão). */
export const validateChargeCheckoutForm = (form: CheckoutFormState): Record<string, string> => (
  validatePaymentStep(
    form.paymentMethod,
    form.card,
    form.payer,
    false,
  )
);

export const resolveCheckoutPersonalFieldError = (
  field: keyof CheckoutPersonalData,
  data: CheckoutPersonalData,
  addressCountry?: string,
): string => validatePersonalDataStep(data, addressCountry)[field] ?? '';

export const resolveLeadQualificationPersonalFieldError = (
  field: keyof CheckoutPersonalData,
  data: CheckoutPersonalData,
): string => validateLeadQualificationPersonalData(data)[field] ?? '';

export const resolveCheckoutAddressFieldError = (
  field: keyof CheckoutAddressData,
  data: CheckoutAddressData,
): string => validateAddressStep(data)[field] ?? '';

const CHECKOUT_CARD_FIELD_ERROR_KEYS: Record<keyof CheckoutCardData, string> = {
  holderName: 'cardHolderName',
  number: 'cardNumber',
  expiry: 'cardExpiry',
  cvv: 'cardCvv',
};

export const resolveCheckoutCardFieldError = (
  field: keyof CheckoutCardData,
  card: CheckoutCardData,
): string => {
  const errors = validateCardData(card);
  return errors[CHECKOUT_CARD_FIELD_ERROR_KEYS[field]] ?? '';
};

const CHECKOUT_PAYER_FIELD_ERROR_KEYS: Record<keyof CheckoutPayerData, string> = {
  name: 'payerName',
  email: 'payerEmail',
  cpf: 'payerCpf',
  postalCode: 'payerPostalCode',
  addressNumber: 'payerAddressNumber',
  addressComplement: 'payerAddressComplement',
  callingCode: 'payerCallingCode',
  phone: 'payerPhone',
};

export const resolveCheckoutPayerFieldError = (
  field: keyof CheckoutPayerData,
  payer: CheckoutPayerData,
): string => {
  const errors = validatePayerData(payer);
  return errors[CHECKOUT_PAYER_FIELD_ERROR_KEYS[field]] ?? '';
};

export const resolveCheckoutPaymentMethodError = (
  paymentMethod: PaymentMethod | null,
  card: CheckoutCardData,
  payer?: CheckoutPayerData,
): string => validatePaymentStep(paymentMethod, card, payer).paymentMethod ?? '';

export const validateCheckoutStep = (
  stepId: CheckoutStepId,
  form: CheckoutFormState,
): Record<string, string> => {
  switch (stepId) {
    case 'personal':
      return validatePersonalDataStep(form.personal, form.address.country);
    case 'address':
      return validateAddressStep(form.address);
    case 'plan':
      return {};
    case 'payment':
      return validatePaymentStep(
        form.paymentMethod,
        form.card,
        form.payer,
        form.useStudentDataAsPayer,
        form.personal,
        form.address,
      );
    case 'review':
      return {};
    default:
      return {};
  }
};

const normalizeBenefitItems = (items: unknown[]): string[] => (
  items
    .map((item) => (typeof item === 'string' ? item.trim() : String(item).trim()))
    .filter(Boolean)
);

function parsePostgresTextArray(value: string): string[] | null {
  const trimmed = value.trim();

  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return null;
  }

  const inner = trimmed.slice(1, -1).trim();

  if (!inner) {
    return [];
  }

  if (inner.includes('"')) {
    try {
      const parsed = JSON.parse(`[${inner}]`) as unknown;

      if (Array.isArray(parsed)) {
        return normalizeBenefitItems(parsed);
      }
    } catch {
      // Fall back to manual parsing below.
    }
  }

  const items: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let index = 0; index < inner.length; index += 1) {
    const char = inner[index];

    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }

    if (char === ',' && !inQuotes) {
      if (current.trim()) {
        items.push(current.trim());
      }
      current = '';
      continue;
    }

    current += char;
  }

  if (current.trim()) {
    items.push(current.trim());
  }

  return items;
}

export const parsePlanBenefits = (benefits: unknown): string[] => {
  if (benefits == null) {
    return [];
  }

  if (Array.isArray(benefits)) {
    return normalizeBenefitItems(benefits);
  }

  if (typeof benefits !== 'string') {
    return [];
  }

  const trimmed = benefits.trim();

  if (!trimmed) {
    return [];
  }

  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;

      if (Array.isArray(parsed)) {
        return normalizeBenefitItems(parsed);
      }
    } catch {
      // Fall back to plain-text parsing below.
    }
  }

  const postgresItems = parsePostgresTextArray(trimmed);

  if (postgresItems) {
    return postgresItems;
  }

  return trimmed
    .split(/\r?\n|[;•]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

export const serializePlanBenefits = (benefits: unknown): string | null => {
  const items = parsePlanBenefits(benefits);
  return items.length > 0 ? items.join('\n') : null;
};

export const calculateCheckoutTotalCents = (
  plan: CheckoutPlanLike | null,
  discountCents: number,
): number => {
  if (!plan) {
    return 0;
  }

  return Math.max(plan.price_cents - discountCents, 0);
};

const normalizeBillingCycle = (billingCycle: string): string => billingCycle.trim().toLowerCase();

export const getBillingCycleLabel = (billingCycle: string): string => {
  const trimmed = billingCycle.trim();
  const normalized = normalizeBillingCycle(trimmed);

  return CYCLE_LABELS[normalized]
    ?? CYCLE_LABELS[trimmed]
    ?? CYCLE_LABELS[trimmed.toUpperCase()]
    ?? trimmed;
};

export const getBillingCyclePeriodLabel = (billingCycle: string): string => {
  const normalized = normalizeBillingCycle(billingCycle);

  switch (normalized) {
    case 'monthly':
      return 'mês';
    case 'bimonthly':
      return 'bimestre';
    case 'weekly':
      return 'semana';
    case 'biweekly':
      return 'quinzena';
    case 'quarterly':
      return 'trimestre';
    case 'semestral':
    case 'semiannually':
      return 'semestre';
    case 'yearly':
      return 'ano';
    default:
      return getBillingCycleLabel(billingCycle).toLowerCase();
  }
};

export const getBillingCyclePriceSuffix = (billingCycle: string): string => {
  const normalized = normalizeBillingCycle(billingCycle);

  switch (normalized) {
    case 'monthly':
      return '/mês';
    case 'bimonthly':
      return '/bimestre';
    case 'weekly':
      return '/semana';
    case 'biweekly':
      return '/quinzena';
    case 'quarterly':
      return '/trimestre';
    case 'semestral':
    case 'semiannually':
      return '/semestre';
    case 'yearly':
      return '/ano';
    default:
      return '';
  }
};

export const isSinglePaymentPlan = (plan: CheckoutPlanLike): boolean => {
  return Boolean(plan.is_single_payment);
};

export const getRecurringBillingLabel = (plan: CheckoutPlanLike): string => {
  if (isSinglePaymentPlan(plan)) {
    return 'Pagamento à vista';
  }

  const cycleLabel = getBillingCycleLabel(plan.billing_cycle).toLowerCase();

  if (plan.auto_renew) {
    return `Cobrança recorrente ${cycleLabel}`;
  }

  return `Pagamento ${cycleLabel}`;
};

export const buildCheckoutSubmissionPayload = (
  form: CheckoutFormState,
  selectedPlan: CheckoutSelectedPlan,
  options?: {
    updateActionToken?: string | null;
    trackingRefToken?: string | null;
    stripeReturnUrl?: string | null;
  },
): CheckoutSubmissionPayload => {
  const payload: CheckoutSubmissionPayload = {
    personal: {
      ...form.personal,
      fullName: trimCheckoutFullName(form.personal.fullName),
      cpf: isBrazilCheckoutCountry(form.address.country) ? unmaskCpf(form.personal.cpf) : unmaskCpf(form.personal.cpf) || '',
      phone: unmaskPhone(form.personal.phone),
    },
    address: form.address,
    selectedPlan: {
      hash: selectedPlan.hash,
    },
    paymentMethod: form.paymentMethod!,
  };

  if (options?.updateActionToken?.trim()) {
    payload.updateAction = { token: options.updateActionToken.trim() };
  }

  if (options?.trackingRefToken?.trim()) {
    payload.trackingRef = { token: options.trackingRefToken.trim() };
  }

  const checkoutProvider = resolveCheckoutPaymentProvider(form.address.country);

  if (checkoutProvider === 'stripe' && options?.stripeReturnUrl?.trim()) {
    payload.stripeReturnUrl = options.stripeReturnUrl.trim();
  }

  if (checkoutProvider === 'asaas' && form.paymentMethod === 'credit_card') {
    const [expiryMonth = '', expiryYear = ''] = form.card.expiry.split('/');
    payload.creditCard = {
      holderName: form.card.holderName.trim(),
      number: unmaskCardNumber(form.card.number),
      expiryMonth,
      expiryYear: /^\d{2}$/.test(expiryYear) ? `20${expiryYear}` : expiryYear,
      cvv: form.card.cvv,
    };

    const payer = resolveCheckoutPayerForSubmission(form);
    const payerPhone = unmaskPhone(payer.phone);
    const payerCallingCode = payer.callingCode.split('_')[0].replace(/\D/g, '') || '55';
    const phoneWithCountry = `${payerCallingCode}${payerPhone}`;
    const complement = payer.addressComplement.trim();

    payload.creditCardHolderInfo = {
      name: payer.name.trim(),
      email: payer.email.trim(),
      cpfCnpj: unmaskCpf(payer.cpf),
      postalCode: payer.postalCode.replace(/\D/g, ''),
      addressNumber: payer.addressNumber.trim(),
      addressComplement: complement || null,
      phone: phoneWithCountry,
      mobilePhone: phoneWithCountry,
    };
  }

  return payload;
};

export const buildChargeCheckoutSubmissionPayload = (
  form: CheckoutFormState,
  selectedPlan: CheckoutSelectedPlan,
  chargeToken: string,
): {
  chargeToken: string;
  selectedPlan: { hash: string };
  paymentMethod: PaymentMethod;
  creditCard?: CheckoutSubmissionPayload['creditCard'];
  creditCardHolderInfo?: CheckoutSubmissionPayload['creditCardHolderInfo'];
} => {
  const payload: {
    chargeToken: string;
    selectedPlan: { hash: string };
    paymentMethod: PaymentMethod;
    creditCard?: CheckoutSubmissionPayload['creditCard'];
    creditCardHolderInfo?: CheckoutSubmissionPayload['creditCardHolderInfo'];
  } = {
    chargeToken: chargeToken.trim(),
    selectedPlan: {
      hash: selectedPlan.hash,
    },
    paymentMethod: form.paymentMethod!,
  };

  if (form.paymentMethod === 'credit_card') {
    const [expiryMonth = '', expiryYear = ''] = form.card.expiry.split('/');
    payload.creditCard = {
      holderName: form.card.holderName.trim(),
      number: unmaskCardNumber(form.card.number),
      expiryMonth,
      expiryYear: /^\d{2}$/.test(expiryYear) ? `20${expiryYear}` : expiryYear,
      cvv: form.card.cvv,
    };

    const payer = form.payer;
    const payerPhone = unmaskPhone(payer.phone);
    const payerCallingCode = payer.callingCode.split('_')[0].replace(/\D/g, '') || '55';
    const phoneWithCountry = `${payerCallingCode}${payerPhone}`;
    const complement = payer.addressComplement.trim();

    payload.creditCardHolderInfo = {
      name: payer.name.trim(),
      email: payer.email.trim(),
      cpfCnpj: unmaskCpf(payer.cpf),
      postalCode: payer.postalCode.replace(/\D/g, ''),
      addressNumber: payer.addressNumber.trim(),
      addressComplement: complement || null,
      phone: phoneWithCountry,
      mobilePhone: phoneWithCountry,
    };
  }

  return payload;
};

/**
 * Placeholder payment processor — swap implementation when integrating Stripe, Mercado Pago, etc.
 */
export const processCheckoutPayment = async (
  payload: CheckoutSubmissionPayload,
): Promise<CheckoutPaymentResult> => {
  await new Promise((resolve) => {
    window.setTimeout(resolve, 1200);
  });

  return {
    success: true,
    message: `Contratação registrada com sucesso. Em breve você receberá instruções de pagamento via ${payload.paymentMethod}.`,
    externalReference: `checkout-${Date.now()}`,
  };
};

export const getPaymentMethodLabel = (method: PaymentMethod | null): string => {
  switch (method) {
    case 'credit_card':
      return 'Cartão de crédito';
    case 'pix':
      return 'Pix';
    case 'boleto':
      return 'Boleto';
    default:
      return '—';
  }
};

export const maskCardNumberForDisplay = (cardNumber: string): string => {
  const digits = unmaskCardNumber(cardNumber);

  if (digits.length < 4) {
    return '****';
  }

  return `**** **** **** ${digits.slice(-4)}`;
};

export const formatCheckoutPhoneDisplay = (
  phone: string,
  callingCode: string,
): string => {
  const trimmedPhone = phone.trim();
  if (!trimmedPhone) {
    return '—';
  }

  const code = callingCode.trim() || '55';
  return `+${code} ${trimmedPhone}`;
};

export const formatLoyaltyLabel = (loyaltyMonths: number): string => {
  if (loyaltyMonths <= 0) {
    return 'Sem período de fidelidade';
  }

  if (loyaltyMonths === 1) {
    return '1 mês de fidelidade';
  }

  return `${loyaltyMonths} meses de fidelidade`;
};
